/**
 * FreeSubjectPickerModal - first-time pick + swap UI for free-tier subject
 * gate-keeping (2026-04-28).
 *
 * Two modes:
 *   - First pick: opened automatically when /subjects returns
 *     `subject_gate.needs_initial_pick=true`. Cannot be dismissed without
 *     selecting exactly N subjects (N = subject_gate.limit). Title:
 *     "Escolhe as tuas N disciplinas".
 *   - Swap: opened from the ProfilePage "Trocar disciplinas" button when
 *     the cooldown has elapsed. Pre-selects the current picks so the
 *     student can adjust. Title: "Trocar disciplinas".
 *
 * Submits to POST /students/me/free-subjects. On 429 (cooldown active -
 * shouldn't happen if the parent gated correctly, but defensive) shows
 * the next-swap date inline.
 */

import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

export interface PickableSubject {
  id:           string
  code:         string
  name:         string
  description?: string | null
}

export interface FreeSubjectPickerModalProps {
  /** All subjects available to pick from (regardless of locked state). */
  subjects:        PickableSubject[]
  /** How many subjects the student must pick (= subject_gate.limit). */
  limit:           number
  /** Currently picked codes. Empty array on first-pick flow. */
  initialCodes:    string[]
  /** "first-pick" | "swap" - drives copy + dismissibility. */
  mode:            'first-pick' | 'swap'
  /** Optional custom title/description for non-12ª gate variants. */
  title?:          string
  description?:    string
  /** Optional custom endpoint. Defaults to the existing free-tier save path. */
  savePath?:       string
  /** Cooldown signal from subjectGate. When false we surface a soft hint
   *  explaining that the student can ADD into unused slots but can't
   *  swap an existing pick out until the cooldown elapses. Backend is
   *  authoritative — frontend uncheck+save during cooldown will be
   *  rejected with the existing 429 message. */
  canSwapNow?:     boolean
  /** Used by the cooldown soft hint to show the next-swap date. */
  nextSwapAt?:     string | null
  /** Called after a successful save with the new picks. The parent should
   *  refetch /subjects to update the locked flags. */
  onSaved:         (codes: string[]) => void
  /** Only called in `swap` mode - first-pick is mandatory. */
  onClose?:        () => void
}

export const FreeSubjectPickerModal: React.FC<FreeSubjectPickerModalProps> = ({
  subjects, limit, initialCodes, mode, title, description, savePath = '/students/me/free-subjects',
  canSwapNow, nextSwapAt, onSaved, onClose,
}) => {
  const [picked, setPicked] = useState<string[]>(initialCodes)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  useEffect(() => { setPicked(initialCodes) }, [initialCodes])

  const toggle = (code: string) => {
    setError(null)
    setPicked(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code)
      if (prev.length >= limit) {
        setError(`Só podes escolher ${limit} disciplinas. Desselecciona uma para escolher outra.`)
        return prev
      }
      return [...prev, code]
    })
  }

  const handleSave = async () => {
    // Subject picker flex (Slice 0, 2026-05-18). Was `!== limit`, forced
    // students to pad up to the cap. New rule: at least 1, up to limit.
    if (picked.length < 1 || picked.length > limit) {
      setError(`Escolhe entre 1 e ${limit} disciplinas.`)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api.post(savePath, { codes: picked })
      onSaved(picked)
    } catch (err: any) {
      const data = err?.data as { error?: string; next_swap_at?: string; message?: string } | undefined
      if (data?.error === 'swap_cooldown_active' && data.next_swap_at) {
        const next = new Date(data.next_swap_at).toLocaleDateString('pt-MZ')
        setError(`Em espera. Próxima troca em ${next}.`)
      } else {
        setError(data?.message ?? err?.message ?? 'Erro ao gravar - tenta novamente.')
      }
    } finally {
      setSaving(false)
    }
  }

  const isFirstPick = mode === 'first-pick'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => { if (!isFirstPick && !saving && onClose) onClose() }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl overflow-y-auto max-h-[90vh] max-w-xl w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {title ?? (isFirstPick ? `Escolhe até ${limit} disciplinas` : 'Trocar disciplinas')}
          </h3>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            {description ?? (isFirstPick
              ? `O plano gratuito dá-te acesso a ${limit} disciplinas. Escolhe as que mais precisas (entre 1 e ${limit}) - podes trocar mais tarde.`
              : `Selecciona até ${limit} disciplinas. As outras ficam bloqueadas até à próxima troca.`)}
          </p>
          {/* Soft hint when student is mid-cooldown in swap mode. Backend
              is authoritative — uncheck+save during cooldown returns 429
              (swap_cooldown_active). This hint just sets the expectation
              up-front so the rejection isn't a surprise. */}
          {!isFirstPick && canSwapNow === false && initialCodes.length < limit && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Período de troca ativo:</span>{' '}
              podes ADICIONAR disciplinas até atingires o limite ({initialCodes.length}/{limit}), mas não substituir as actuais.
              {nextSwapAt && (
                <> Próxima troca livre em {new Date(nextSwapAt).toLocaleDateString('pt-MZ')}.</>
              )}
            </div>
          )}
          {!isFirstPick && canSwapNow === false && initialCodes.length >= limit && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Período de troca ativo:</span>{' '}
              já tens {limit} disciplinas escolhidas. Vais poder trocá-las
              {nextSwapAt && <> em {new Date(nextSwapAt).toLocaleDateString('pt-MZ')}</>}.
            </div>
          )}
        </div>

        <div className="p-5 space-y-2">
          {subjects.map(s => {
            const checked = picked.includes(s.code)
            return (
              <label
                key={s.code}
                className={`flex items-start gap-3 px-3 py-3 rounded-xl border cursor-pointer transition-colors ${
                  checked
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(s.code)}
                  className="mt-0.5 w-4 h-4 accent-indigo-600 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{s.name}</span>
                    <span className="text-[11px] font-mono text-gray-400">{s.code}</span>
                  </div>
                  {s.description && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{s.description}</p>
                  )}
                </div>
              </label>
            )
          })}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mt-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            <span className="font-semibold text-gray-800">{picked.length}</span>
            {' '}de {limit} máx.
          </span>
          <div className="flex gap-2">
            {!isFirstPick && (
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || picked.length < 1 || picked.length > limit}
              className="px-4 py-1.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'A gravar…' : isFirstPick ? 'Confirmar' : 'Gravar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
